import React, { useRef, useMemo } from "react";
import { Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import { Box, Button, Card, CheckboxField, DateField, Typography } from "@curves/ui-base";
import { CyclePurchaseSummaryCard, TermSection } from "@icoach/healthyCyclePurchase/components";
import { useCyclePurchaseEffectOrderData, useCyclePurchaseEffectOrderSubmit, useCyclePurchaseEffectRange } from "@icoach/healthyCyclePurchase/hooks";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { EmployeeSelect, FocusModeToggle, ReadTextField } from "@icoach/components";
import { displayDateText, getAddMonths, getFirstOfMonth, getNowTime, initialDate, isValidDate, parseDate } from "@util/moment";
import { EFFECT_CONTENT_TYPE } from "@constants/";

/**
 * 循環訂單恢復申請頁面
 * 獨立申請表單，使用變更單 API
 */
const CyclePurchaseResumePage = () => {
	const { cycleOrderID, effectID = 0 } = useParams();

	// 使用 hooks 管理資料
	const { cycleOrder, effectOrder, resource, memberID, clubID, shippingDay } = useCyclePurchaseEffectOrderData({
		cycleOrderID,
		effectID,
	});

	// 提交變更單 hook
	const { submit, validationErrors, clearValidationErrors } = useCyclePurchaseEffectOrderSubmit({
		effectID,
		memberID,
	});

	// 使用 hook 取得變更單的生效日期和下次出貨日期
	const { effectDate, nextShippingDate, getEffectRangeDebounced } = useCyclePurchaseEffectRange();

	// ref
	const reSuspendDateRef = useRef(null);
	const confirmRef = useRef(null);
	const termRef = useRef(null);
	const effectCreateDateRef = useRef(null);
	const employeeRef = useRef(null);

	// 聚焦模式 ref
	const focusModeRef = useRef(false);

	useInactivityTimerToggle();

	// 根據 effectID 決定預設值來源：effectID = 0 使用 cycleOrder，否則使用 effectOrder
	const defaultSource = useMemo(() => {
		const isNewEffectOrder = Number(effectID) === 0;
		return isNewEffectOrder ? cycleOrder : effectOrder;
	}, [effectID, cycleOrder, effectOrder]);

	const getParams = () => {
		let result = {
			effectType: [EFFECT_CONTENT_TYPE.RESTOP], // 恢復申請的 effectType
			effectDate,
			shippingDay,
			clubID,
			memberID,
			cycleOrderID,
			originalSuspendStartDate: cycleOrder?.suspendStartDate, // 後端記錄用
			originalSuspendEndDate: cycleOrder?.suspendEndDate, // 後端記錄用
		};

		if (termRef.current && termRef.current.getResult) result = Object.assign(result, termRef.current.getResult());

		if (reSuspendDateRef.current && reSuspendDateRef.current.getResult) {
			result.reSuspendDate = reSuspendDateRef.current.getResult();
		}

		if (effectCreateDateRef.current && effectCreateDateRef.current.getResult) {
			result.effectCreateDate = effectCreateDateRef.current.getResult();
		}

		if (employeeRef.current && employeeRef.current.getResult) {
			result.employeeID = employeeRef.current.getResult() || 0;
		}

		return result;
	};

	const handleReSuspendDateChange = (_, date) => {
		// 變更 reSuspendDate 時，重新計算 effectDate 和 nextShippingDate
		if (isValidDate(date)) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				reSuspendStartDate: date,
			});
		}
	};

	const handleSubmit = () => {
		clearValidationErrors();
		const isError = refIsRequiredError(reSuspendDateRef, confirmRef, effectCreateDateRef, employeeRef);
		if (!isError) {
			const params = getParams();
			submit(params);
		}
	};

	return (
		<Box className="container main-container-spacing cycle-purchase-resume-page">
			<Box component={"header"} className={"page-header"}>
				<Typography className="page-header-title" variant="h3">
					健康產品變更單
				</Typography>
			</Box>
			<FocusModeToggle ref={focusModeRef}>
				<CyclePurchaseSummaryCard sourceData={cycleOrder} />
				<Card className={"order-info-card mb-4"}>
					<Card.CardHeader title="訂單恢復申請" className="bg-light-active" />
					<Card.CardContent className="p-4">
						<Grid spacing={3} container>
							<Grid xs={6} item>
								<ReadTextField label="暫停期間">
									{displayDateText(cycleOrder?.suspendStartDate, cycleOrder?.suspendEndDate, {
										format: "YYYY [年] M [月]",
									})}
								</ReadTextField>
							</Grid>
							<Grid xs={6} item>
								<ReadTextField label="下一次出貨月份">{parseDate(getAddMonths(cycleOrder?.suspendEndDate, 1), "YYYY [年] M [月]")}</ReadTextField>
							</Grid>
							<Grid xs={6} item>
								<DateField
									label="更新出貨月份"
									ref={reSuspendDateRef}
									key={initialDate(defaultSource?.reSuspendDate || cycleOrder?.reSuspendDate)}
									defaultValue={initialDate(defaultSource?.reSuspendDate || cycleOrder?.reSuspendDate) || initialDate(getNowTime())}
									views={["year", "month"]}
									openTo="month"
									format="YYYY/MM"
									mask="____/__"
									minDate={getFirstOfMonth(getNowTime())}
									maxDate={cycleOrder?.suspendEndDate}
									placeholder="請選擇月份"
									onChange={handleReSuspendDateChange}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={6} item />
							{isValidDate(nextShippingDate) && (
								<Grid xs={12} item>
									<CheckboxField ref={confirmRef} required>
										<CheckboxField.Checkbox value={true} label={`我已確認將於${parseDate(nextShippingDate, "YYYY [年] M [月]")}恢復出貨。`} />
									</CheckboxField>
								</Grid>
							)}
							<Grid xs={4} item>
								<DateField
									label="建單日期"
									ref={effectCreateDateRef}
									key={initialDate(defaultSource?.effectCreateDate)}
									defaultValue={initialDate(defaultSource?.effectCreateDate) || initialDate(getNowTime())}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<DateField label="變更生效日期" key={initialDate(effectDate)} defaultValue={initialDate(effectDate)} fullWidth required disabled />
							</Grid>
							<Grid xs={4} item>
								<EmployeeSelect label="擔當" ref={employeeRef} options={resource?.coachOptions ?? []} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
				</Card>
				{/* 訂購條款 */}
				<TermSection className={"mb-4"} ref={termRef} nextShippingDate={nextShippingDate} errorModule={validationErrors} />
				<Box className={"text-right btn-group px-4 pb-3"}>
					<Button variant={"contained"} onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmit)}>
						確認送出
					</Button>
				</Box>
			</FocusModeToggle>
		</Box>
	);
};

export default CyclePurchaseResumePage;
